/**
 * Local gym reminders.
 *
 * Honest limitation: this is a web app with no push server, so notifications
 * can only be scheduled while a tab is alive. If you install it to your home
 * screen and open it at some point during the day, you'll get that day's
 * reminder; if you never open it, you won't. Anything more reliable needs
 * either a push backend or a native app, and neither is worth it for a nudge
 * you can also get from a repeating phone alarm.
 *
 * The Settings screen says as much rather than pretending otherwise.
 */

export type PermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export function notificationSupport(): PermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission as PermissionState;
}

export async function requestPermission(): Promise<PermissionState> {
  if (notificationSupport() === 'unsupported') return 'unsupported';
  const result = await Notification.requestPermission();
  return result as PermissionState;
}

let timer: number | undefined;

/**
 * Schedules the next reminder for `time` ("18:00") minus `leadMinutes`, on the
 * given weekdays. Replaces any previously scheduled one.
 */
export function scheduleNextReminder(options: {
  enabled: boolean;
  days: number[];
  time: string;
  leadMinutes: number;
  title: string;
  body: string;
}): Date | null {
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timer = undefined;
  }

  if (!options.enabled || notificationSupport() !== 'granted' || options.days.length === 0) {
    return null;
  }

  const target = nextOccurrence(options.days, options.time, options.leadMinutes);
  if (!target) return null;

  const delay = target.getTime() - Date.now();
  // setTimeout is a 32-bit signed int in ms — anything past ~24.8 days
  // overflows and fires immediately. We never schedule more than a week out,
  // so this is just a guard.
  if (delay <= 0 || delay > 7 * 86_400_000) return null;

  timer = window.setTimeout(() => {
    try {
      new Notification(options.title, { body: options.body, tag: 'iron-ledger-gym' });
    } catch {
      // Some browsers only allow notifications from a service worker context.
      // Nothing useful to do here — the in-app schedule still shows.
    }
  }, delay);

  return target;
}

export function nextOccurrence(days: number[], time: string, leadMinutes: number): Date | null {
  const [hours, minutes] = time.split(':').map(Number);
  if (hours === undefined || minutes === undefined) return null;

  const now = new Date();
  for (let offset = 0; offset < 8; offset++) {
    const candidate = new Date(now);
    candidate.setDate(candidate.getDate() + offset);
    if (!days.includes(candidate.getDay())) continue;
    candidate.setHours(hours, minutes, 0, 0);
    candidate.setMinutes(candidate.getMinutes() - leadMinutes);
    if (candidate.getTime() > now.getTime()) return candidate;
  }
  return null;
}

export function cancelReminder(): void {
  if (timer !== undefined) {
    window.clearTimeout(timer);
    timer = undefined;
  }
}
